import React, { useState } from 'react'
import MultiPolygonEditor from './MultiPolygonEditor'

interface AreaWorking {
  id?: number
  name: string
  multi: number[][][]
}

interface CombinedBoundaryEditorProps {
  siteName: string
  siteMulti: number[][][]
  areas: AreaWorking[]
  onSave: (
    siteMulti: number[][][],
    areas: AreaWorking[],
    deletedAreaIds: number[]
  ) => Promise<{ siteMulti: number[][][]; areas: AreaWorking[] } | void> | { siteMulti: number[][][]; areas: AreaWorking[] } | void
  onClose: () => void
  initialMode?: 'overview' | 'site' | 'area'
  initialAreaIndex?: number
}

const CombinedBoundaryEditor: React.FC<CombinedBoundaryEditorProps> = ({ siteName, siteMulti, areas, onSave, onClose, initialMode = 'overview', initialAreaIndex = 0 }) => {
  const [mode, setMode] = useState<'overview' | 'site' | 'area'>(initialMode)
  const [workingSite, setWorkingSite] = useState<number[][][]>(siteMulti || [])
  const [workingAreas, setWorkingAreas] = useState<AreaWorking[]>(areas || [])
  const [selectedAreaIdx, setSelectedAreaIdx] = useState<number>(initialAreaIndex)
  const [renaming, setRenaming] = useState(false)
  const [tempName, setTempName] = useState('')
  const [deletedAreaIds, setDeletedAreaIds] = useState<number[]>([])
  const [locating, setLocating] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)
  const [mapRef, setMapRef] = useState<any>(null)
  const [didInitialFit, setDidInitialFit] = useState(false)

  function getBoundsFromMulti(multi: number[][][]): [[number, number],[number, number]] | null {
    try {
      let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity
      ;(multi || []).forEach(poly => {
        (poly || []).forEach(([lat, lng]) => {
          if (lat < minLat) minLat = lat
          if (lng < minLng) minLng = lng
          if (lat > maxLat) maxLat = lat
          if (lng > maxLng) maxLng = lng
        })
      })
      if (!isFinite(minLat) || !isFinite(minLng) || !isFinite(maxLat) || !isFinite(maxLng)) return null
      return [[minLat, minLng],[maxLat, maxLng]]
    } catch {
      return null
    }
  }

  // On open, center map to site bounds if present; otherwise to all areas
  React.useEffect(() => {
    if (!mapRef || didInitialFit) return
    let bounds = getBoundsFromMulti(workingSite)
    if (!bounds) {
      const allAreas: number[][][] = []
      ;(workingAreas || []).forEach(a => { if (a && Array.isArray(a.multi)) allAreas.push(...a.multi) })
      bounds = getBoundsFromMulti(allAreas)
    }
    if (bounds) {
      try {
        mapRef.fitBounds(bounds as any, { padding: [40, 40] })
        setDidInitialFit(true)
      } catch {}
    }
  }, [mapRef, workingSite, workingAreas, didInitialFit])

  const currentArea = workingAreas[selectedAreaIdx]

  const applyEditorChange = (multi: number[][][]) => {
    if (mode === 'site') {
      setWorkingSite(multi)
    } else if (currentArea) {
      const next = [...workingAreas]
      next[selectedAreaIdx] = { ...next[selectedAreaIdx], multi }
      setWorkingAreas(next)
    }
  }

  const addArea = () => {
    const next = [...workingAreas, { name: 'New Area', multi: [] }]
    setWorkingAreas(next)
    setSelectedAreaIdx(next.length - 1)
    setMode('area')
  }

  const deleteArea = () => {
    if (!currentArea) return
    // Track deletion if existing ID
    if (currentArea.id) {
      setDeletedAreaIds(prev => prev.includes(currentArea.id!) ? prev : [...prev, currentArea.id!])
    }
    const next = workingAreas.filter((_, idx) => idx !== selectedAreaIdx)
    setWorkingAreas(next)
    setSelectedAreaIdx(0)
    if (next.length === 0) setMode('site')
  }

  const startRename = () => { setRenaming(true); setTempName(currentArea?.name || '') }
  const applyRename = () => {
    if (!currentArea) return setRenaming(false)
    const next = [...workingAreas]
    next[selectedAreaIdx] = { ...next[selectedAreaIdx], name: tempName || next[selectedAreaIdx].name }
    setWorkingAreas(next)
    setRenaming(false)
  }

  const saveAll = async () => {
    const updated = await onSave(workingSite, workingAreas, deletedAreaIds)
    if (updated && updated.siteMulti && updated.areas) {
      setWorkingSite(updated.siteMulti)
      setWorkingAreas(updated.areas)
      setDeletedAreaIds([]) // clear deletions after successful save
    }
  }

  const editorMulti = mode === 'site' ? workingSite : (currentArea?.multi || [])

  return (
    <div className="polygon-editor-overlay">
      <div className="polygon-editor-container" style={{ width: '90vw', maxHeight: '90vh' }}>
        <div className="polygon-editor-header">
          <div className="boundary-header-bar">
            <div className="left">
              <div className="title">{siteName}</div>
              <div className="tabs">
                <button className={`tab ${mode==='overview'?'active':''}`} onClick={() => setMode('overview')}>Overview</button>
                <button className={`tab ${mode==='site'?'active':''}`} onClick={() => setMode('site')}>Site</button>
                <button className={`tab ${mode==='area'?'active':''}`} onClick={() => setMode('area')}>Areas</button>
                {mode==='area' && (
                  <div className="area-tools">
                    <select className="area-select" value={selectedAreaIdx} onChange={(e) => setSelectedAreaIdx(parseInt(e.target.value))}>
                      {workingAreas.map((a, idx) => <option value={idx} key={a.id || idx}>{a.name}</option>)}
                    </select>
                    <button className="btn btn-sm" onClick={addArea}>➕ Add</button>
                    <button className="btn btn-sm" onClick={startRename} disabled={!currentArea}>Rename</button>
                    <button className="btn btn-sm btn-danger" onClick={deleteArea} disabled={!currentArea}>Delete</button>
                  </div>
                )}
              </div>
            </div>
            <div className="right">
              <button
                className="btn"
                onClick={() => {
                  setGeoError(null)
                  setLocating(true)
                  if (!('geolocation' in navigator)) {
                    setGeoError('Geolocation is not supported by your browser.')
                    setLocating(false)
                    return
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const { latitude, longitude } = pos.coords
                      if (mapRef) {
                        mapRef.setView([latitude, longitude], 15)
                      }
                      setLocating(false)
                    },
                    (err) => {
                      console.error('Geolocation error:', err)
                      setGeoError('Unable to get your location. Check permissions.')
                      setLocating(false)
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
                  )
                }}
                disabled={locating}
                title="Center and zoom to my location"
              >
                {locating ? '📍 Locating…' : '📍 Focus on Me'}
              </button>
              <button className="btn btn-success" onClick={saveAll}>💾 Save All</button>
              <button className="close-x" onClick={onClose} aria-label="Close editor" title="Close">×</button>
            </div>
          </div>
        </div>
        {renaming && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0' }}>
            <input value={tempName} onChange={(e) => setTempName(e.target.value)} placeholder="Area name" />
            <button className="btn" onClick={applyRename}>Apply</button>
            <button className="btn" onClick={() => setRenaming(false)}>Cancel</button>
          </div>
        )}
        {geoError && <div className="error" style={{ margin: '0.5rem 1.5rem' }}>{geoError}</div>}
        {mode === 'overview' ? (
          <MultiPolygonEditor
            title={'Overview'}
            initialMultiPolygon={[]}
            onChange={() => {}}
            onClose={() => {}}
            showSaveButton={false}
            embedded={true}
            onMapReady={(m) => setMapRef(m)}
            readonly={true}
            overlaySiteName={siteName}
            overlaySite={workingSite}
            overlayAreas={workingAreas.map(a => ({ name: a.name, multi: a.multi }))}
          />
        ) : (
          <MultiPolygonEditor
            title={mode==='site' ? 'Edit Site' : `Edit Area - ${currentArea?.name || ''}`}
            initialMultiPolygon={editorMulti}
            onChange={applyEditorChange}
            onClose={saveAll}
            showSaveButton={false}
            embedded={true}
            onMapReady={(m) => setMapRef(m)}
            readonly={false}
            overlaySiteName={mode==='site' ? siteName : undefined}
            overlaySite={mode==='site' ? workingSite : undefined}
            overlayAreas={mode==='area' && currentArea ? [{ name: currentArea.name, multi: currentArea.multi }] : undefined}
          />
        )}
      </div>
    </div>
  )
}

export default CombinedBoundaryEditor
